import React, { useMemo, useState, useEffect } from 'react';
import { usePlanetaryPositions } from '../../hooks/usePlanetaryPositions';
import { usePanchanga } from '../../hooks/usePanchanga';
import type { PlanetPosition } from '../../types/api';
import { CONSTELLATION_PATTERNS } from './constellationData';
import {
  getElementForSign,
  classifyPlanetsByElement,
  isInGandanta,
  getGandantaZones,
  computeMoonPhase,
  detectYutis,
  ELEMENT_COLORS,
  PLANET_COLORS,
  PLANET_SANSKRIT_ABBR,
  PLANET_SANSKRIT_NAMES,
  ZODIAC_SYMBOLS,
  ZODIAC_NAMES,
  ZODIAC_SANSKRIT_NAMES,
  NAKSHATRA_DATA,
  type Element,
} from './alignmentUtils';
import { generateInsights, type InsightSeverity } from './insightEngine';
import { InsightsNarrative } from './InsightsNarrative';
import { isKrishnaPaksha } from '../../lib/paksha';

// ── Font constants ──────────────────────────────────────────────────
const FONT = "'Inter', 'Segoe UI', Arial, sans-serif";
const FONT_SERIF = "'Georgia', serif";
const FONT_MONO = "'Courier New', monospace";
const FONT_FRAUNCES = "'Fraunces', Georgia, serif";

// Saffron heading tone (warmer / less flashy than the wheel's gold #FFD700).
// Used for the interactive rail section headings + the page title.
const SAFFRON = '#e8951f';
const SAFFRON_MUTED = 'rgba(232,149,31,0.85)';

// ── Layout constants ────────────────────────────────────────────────
const WIDTH = 1440;
const HEIGHT = 1080;
const TITLE_HEIGHT = 90;
// App header (76) + control bar (56) above the merged Kāla Chakra view. Was 120,
// which clipped the bottom 12px of the instrument and of the scrolling rail.
const MERGED_CHROME_H = 132;

const WHEEL_CX = 960; // Fixed — original position, not centered in reduced WIDTH
const WHEEL_CY = (HEIGHT - TITLE_HEIGHT) * 0.48 + TITLE_HEIGHT;
const OUTER_R = 380;
const INNER_R = 245;
const MID_R = (OUTER_R + INNER_R) / 2;
const BAND_W = OUTER_R - INNER_R;

// Inner-ring zodiac labels — name closer to band, symbol further in
const ZODIAC_NAME_R = INNER_R - 16;
const ZODIAC_SYMBOL_R = INNER_R - 44;

// Outer nakshatra band
const NAKSHATRA_OUTER_R = OUTER_R + 48;
const NAKSHATRA_LABEL_R = OUTER_R + 24;

const GANDANTA_INNER = INNER_R;
const GANDANTA_OUTER = OUTER_R + 4;
const PLANET_CIRCLE_R = 14;
const MIN_PLANET_GAP = PLANET_CIRCLE_R * 2 + 2; // 30px — just clear of overlap
const ALWAYS_RETROGRADE = new Set(['Rahu', 'Ketu']);
const CONJUNCTION_LABEL_R = NAKSHATRA_OUTER_R + 30;

// ── Counter-clockwise geometry ──────────────────────────────────────
// Aries (0°) at 12 o'clock, signs progress counter-clockwise
const degToAngle = (deg: number): number => -deg - 90;
const degToRad = (deg: number): number => (deg * Math.PI) / 180;

const polarToXY = (cx: number, cy: number, r: number, angleDeg: number) => {
  const rad = degToRad(angleDeg);
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

// SVG arc wedge — counter-clockwise outer sweep, clockwise inner return
const buildWedgePath = (
  cx: number, cy: number,
  rInner: number, rOuter: number,
  startDeg: number, endDeg: number,
): string => {
  const a1 = degToAngle(startDeg);
  const a2 = degToAngle(endDeg);
  const os = polarToXY(cx, cy, rOuter, a1);
  const oe = polarToXY(cx, cy, rOuter, a2);
  const ie = polarToXY(cx, cy, rInner, a2);
  const is_ = polarToXY(cx, cy, rInner, a1);
  const la = (endDeg - startDeg) > 180 ? 1 : 0;
  return [
    `M ${os.x} ${os.y}`,
    `A ${rOuter} ${rOuter} 0 ${la} 0 ${oe.x} ${oe.y}`,
    `L ${ie.x} ${ie.y}`,
    `A ${rInner} ${rInner} 0 ${la} 1 ${is_.x} ${is_.y}`,
    'Z',
  ].join(' ');
};

const formatDate = (dateStr?: string): string => {
  if (!dateStr) {
    const now = new Date();
    return now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

// ── Background Starfield ─────────────────────────────────────────────
// Seeded pseudo-random for consistent star placement across renders
const seededRandom = (seed: number) => {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return s / 2147483647;
  };
};

const BACKGROUND_STARS = (() => {
  const rng = seededRandom(42);
  const stars: { x: number; y: number; r: number; opacity: number }[] = [];
  for (let i = 0; i < 300; i++) {
    stars.push({
      x: rng() * WIDTH,
      y: rng() * HEIGHT,
      r: rng() < 0.85 ? 0.4 + rng() * 0.6 : 1 + rng() * 0.8, // mostly tiny, few brighter
      opacity: 0.2 + rng() * 0.6,
    });
  }
  return stars;
})();

const BackgroundStars: React.FC = () => (
  <g>
    {BACKGROUND_STARS.map((s, i) => (
      <circle
        key={`bg-star-${i}`}
        cx={s.x} cy={s.y} r={s.r}
        fill="#c8d8ff" opacity={s.opacity}
      />
    ))}
  </g>
);

// ── SVG Filters ─────────────────────────────────────────────────────
const SvgFilters: React.FC = () => (
  <defs>
    <filter id="starGlow" x="-100%" y="-100%" width="300%" height="300%">
      <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur1" />
      <feGaussianBlur in="SourceGraphic" stdDeviation="1" result="blur2" />
      <feMerge>
        <feMergeNode in="blur1" />
        <feMergeNode in="blur2" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
    <filter id="constellationGlow" x="-200%" y="-200%" width="500%" height="500%">
      <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur1" />
      <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur2" />
      <feMerge>
        <feMergeNode in="blur1" />
        <feMergeNode in="blur2" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
    <filter id="planetGlow" x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur in="SourceGraphic" stdDeviation="3.5" result="blur" />
      <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
    </filter>
    <filter id="textGlow" x="-20%" y="-20%" width="140%" height="140%">
      <feGaussianBlur in="SourceGraphic" stdDeviation="1.5" result="blur" />
      <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
    </filter>
    <filter id="gandantaGlow" x="-10%" y="-10%" width="120%" height="120%">
      <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="blur" />
      <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
    </filter>
    <filter id="symbolGlow" x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur" />
      <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
    </filter>
    {/* Clip path: annular ring between inner and outer radii */}
    <clipPath id="zodiacBandClip">
      <path d={`
        M ${WHEEL_CX + OUTER_R} ${WHEEL_CY}
        A ${OUTER_R} ${OUTER_R} 0 1 1 ${WHEEL_CX - OUTER_R} ${WHEEL_CY}
        A ${OUTER_R} ${OUTER_R} 0 1 1 ${WHEEL_CX + OUTER_R} ${WHEEL_CY}
        M ${WHEEL_CX + INNER_R} ${WHEEL_CY}
        A ${INNER_R} ${INNER_R} 0 1 0 ${WHEEL_CX - INNER_R} ${WHEEL_CY}
        A ${INNER_R} ${INNER_R} 0 1 0 ${WHEEL_CX + INNER_R} ${WHEEL_CY}
      `} fillRule="evenodd" />
    </clipPath>
  </defs>
);

// ── Zodiac Segment Wedge ────────────────────────────────────────────
const ZodiacSegment: React.FC<{ signNumber: number }> = ({ signNumber }) => {
  const startDeg = (signNumber - 1) * 30;
  const endDeg = signNumber * 30;
  const element = getElementForSign(signNumber) as Element;
  const colors = ELEMENT_COLORS[element];
  return (
    <path
      d={buildWedgePath(WHEEL_CX, WHEEL_CY, INNER_R, OUTER_R, startDeg, endDeg)}
      fill={colors.fill} stroke={colors.stroke} strokeWidth={0.8} opacity={0.8}
    />
  );
};

// ── Sign Boundary Lines + Degree Labels ──────────────────────────────
const DEG_LABEL_R = NAKSHATRA_OUTER_R + 16;
const SignBoundaryLines: React.FC = () => (
  <g>
    {Array.from({ length: 12 }, (_, i) => {
      const deg = i * 30;
      const angle = degToAngle(deg);
      const inner = polarToXY(WHEEL_CX, WHEEL_CY, INNER_R, angle);
      const nkOuter = polarToXY(WHEEL_CX, WHEEL_CY, NAKSHATRA_OUTER_R + 8, angle);
      const degPos = polarToXY(WHEEL_CX, WHEEL_CY, DEG_LABEL_R, angle);
      return (
        <g key={`boundary-${i}`}>
          <line
            x1={inner.x} y1={inner.y} x2={nkOuter.x} y2={nkOuter.y}
            stroke="rgba(255,255,255,0.35)" strokeWidth={1.2}
          />
          <text
            x={degPos.x} y={degPos.y}
            textAnchor="middle" dominantBaseline="central"
            fill="#FFD700" fontSize={11}
            fontFamily={FONT}
          >
            {deg}°
          </text>
        </g>
      );
    })}
  </g>
);

// ── Nakshatra Ring (outer band) ─────────────────────────────────────
const NakshatraRing: React.FC = () => {
  const SPAN = 360 / 27;
  return (
    <g>
      {/* Dashed outer border */}
      <circle
        cx={WHEEL_CX} cy={WHEEL_CY} r={NAKSHATRA_OUTER_R}
        fill="none" stroke="rgba(255,215,0,0.35)" strokeWidth={1}
        strokeDasharray="5,4"
      />
      {NAKSHATRA_DATA.map((nak, i) => {
        const boundaryAngle = degToAngle(nak.startDeg);
        const bInner = polarToXY(WHEEL_CX, WHEEL_CY, OUTER_R, boundaryAngle);
        const bOuter = polarToXY(WHEEL_CX, WHEEL_CY, NAKSHATRA_OUTER_R, boundaryAngle);

        const midDeg = nak.startDeg + SPAN / 2;
        const midAngle = degToAngle(midDeg);
        const labelPos = polarToXY(WHEEL_CX, WHEEL_CY, NAKSHATRA_LABEL_R, midAngle);

        // Color-match to the zodiac sign this nakshatra falls in
        const signNum = Math.floor(midDeg / 30) + 1;
        const clampedSign = signNum > 12 ? signNum - 12 : signNum;
        const element = getElementForSign(clampedSign);
        const elColor = ELEMENT_COLORS[element].text;

        // Rotate text tangent to circle; flip for bottom half
        let textRot = -midDeg;
        textRot = ((textRot % 360) + 540) % 360 - 180;
        if (Math.abs(textRot) > 90) textRot += 180;
        textRot = ((textRot % 360) + 540) % 360 - 180;

        return (
          <g key={`nak-${i}`}>
            <line
              x1={bInner.x} y1={bInner.y} x2={bOuter.x} y2={bOuter.y}
              stroke={elColor} strokeWidth={0.8} opacity={0.7}
            />
            <text
              x={labelPos.x} y={labelPos.y}
              textAnchor="middle" dominantBaseline="central"
              fill={elColor} fontSize={16} opacity={1} fontWeight={700}
              fontFamily={FONT}
              transform={`rotate(${textRot}, ${labelPos.x}, ${labelPos.y})`}
            >
              {nak.shortName}
            </text>
          </g>
        );
      })}
    </g>
  );
};

// ── Constellation Stars — dot/line star map ─────────────────────────
const ConstellationStars: React.FC<{ signNumber: number }> = ({ signNumber }) => {
  const pattern = CONSTELLATION_PATTERNS[signNumber];
  if (!pattern) return null;

  const startDeg = (signNumber - 1) * 30;
  const bandHalf = BAND_W / 2;
  const element = getElementForSign(signNumber);
  const elColor = ELEMENT_COLORS[element].text;

  const mapStar = (px: number, py: number) => {
    const angleDeg = degToAngle(startDeg + (px / 100) * 30);
    const r = MID_R - bandHalf * 0.65 + (py / 100) * bandHalf * 1.3;
    return polarToXY(WHEEL_CX, WHEEL_CY, r, angleDeg);
  };

  return (
    <g>
      {/* Connecting lines */}
      {pattern.lines.map(([a, b], i) => {
        const sa = mapStar(pattern.stars[a].x, pattern.stars[a].y);
        const sb = mapStar(pattern.stars[b].x, pattern.stars[b].y);
        return (
          <line
            key={`cline-${signNumber}-${i}`}
            x1={sa.x} y1={sa.y} x2={sb.x} y2={sb.y}
            stroke="rgba(200,220,255,0.35)" strokeWidth={1} opacity={0.5}
          />
        );
      })}

      {/* Stars — glowing dots */}
      {pattern.stars.map((star, i) => {
        const pos = mapStar(star.x, star.y);
        const baseR = 2 + star.brightness * 2.2;
        return (
          <g key={`cstar-${signNumber}-${i}`}>
            {/* Outer glow halo */}
            <circle
              cx={pos.x} cy={pos.y} r={baseR + 2}
              fill={elColor} opacity={0.08 * star.brightness}
              filter="url(#constellationGlow)"
            />
            {/* Core dot */}
            <circle
              cx={pos.x} cy={pos.y} r={baseR}
              fill="#dde6ff" opacity={0.4 + star.brightness * 0.2}
              filter="url(#starGlow)"
            />
            {/* Hot center */}
            <circle
              cx={pos.x} cy={pos.y} r={baseR * 0.4}
              fill="#ffffff" opacity={0.55}
            />
          </g>
        );
      })}
    </g>
  );
};

// ── Zodiac Figure Image (background overlay behind stars) ───────────
const FIGURE_SIZE = BAND_W * 1.25;
// Per-sign scale overrides (1.0 = default, <1.0 = smaller)
const FIGURE_SCALE: Record<number, number> = {
  9: 0.9,    // Sagittarius — tall figure clips
  10: 0.82,  // Capricorn — tall figure clips
};
const ZodiacFigure: React.FC<{ signNumber: number }> = ({ signNumber }) => {
  const midDeg = (signNumber - 1) * 30 + 15;
  const angle = degToAngle(midDeg);
  const pos = polarToXY(WHEEL_CX, WHEEL_CY, MID_R, angle);
  const figureRotation = -midDeg;
  const size = FIGURE_SIZE * (FIGURE_SCALE[signNumber] ?? 1);

  return (
    <g
      transform={`translate(${pos.x}, ${pos.y}) rotate(${figureRotation})`}
      opacity={0.35}
    >
      <image
        href={`/zodiac/${ZODIAC_NAMES[signNumber]}.png`}
        x={-size / 2}
        y={-size / 2}
        width={size}
        height={size}
        preserveAspectRatio="xMidYMid meet"
        filter="url(#symbolGlow)"
      />
    </g>
  );
};

// ── Zodiac Labels (inner ring) — name closer to band, symbol further in ─────
const ZodiacLabel: React.FC<{ signNumber: number }> = ({ signNumber }) => {
  const midDeg = (signNumber - 1) * 30 + 15;
  const angle = degToAngle(midDeg);
  const symbolPos = polarToXY(WHEEL_CX, WHEEL_CY, ZODIAC_SYMBOL_R, angle);
  const namePos = polarToXY(WHEEL_CX, WHEEL_CY, ZODIAC_NAME_R, angle);
  const element = getElementForSign(signNumber);
  const color = ELEMENT_COLORS[element].text;

  // Tangential rotation: text follows the curve, flipped for readability
  let textRot = -midDeg;
  textRot = ((textRot % 360) + 540) % 360 - 180;
  if (Math.abs(textRot) > 90) textRot += 180;
  textRot = ((textRot % 360) + 540) % 360 - 180;

  return (
    <g>
      <text
        x={symbolPos.x} y={symbolPos.y}
        textAnchor="middle" dominantBaseline="central"
        fill={color} fontSize={20} fontFamily="serif"
        filter="url(#textGlow)" opacity={1}
        transform={`rotate(${textRot}, ${symbolPos.x}, ${symbolPos.y})`}
      >
        {ZODIAC_SYMBOLS[signNumber]}
      </text>
      <text
        x={namePos.x} y={namePos.y}
        textAnchor="middle" dominantBaseline="central"
        fill={color} fontSize={18} fontFamily={FONT}
        fontWeight={700} opacity={1}
        transform={`rotate(${textRot}, ${namePos.x}, ${namePos.y})`}
      >
        {ZODIAC_SANSKRIT_NAMES[signNumber]}
      </text>
    </g>
  );
};

// ── Gandanta Zone Overlays ──────────────────────────────────────────
const GandantaOverlay: React.FC = () => {
  const zones = getGandantaZones();
  return (
    <g>
      {zones.map((zone, i) => {
        if (zone.label === 'Pisces-Aries') {
          return (
            <g key={`gandanta-${i}`}>
              <path
                d={buildWedgePath(WHEEL_CX, WHEEL_CY, GANDANTA_INNER, GANDANTA_OUTER, zone.startDeg, 360)}
                fill="rgba(255,30,30,0.2)" filter="url(#gandantaGlow)"
              />
              <path
                d={buildWedgePath(WHEEL_CX, WHEEL_CY, GANDANTA_INNER, GANDANTA_OUTER, 0, zone.endDeg)}
                fill="rgba(255,30,30,0.2)" filter="url(#gandantaGlow)"
              />
            </g>
          );
        }
        return (
          <path
            key={`gandanta-${i}`}
            d={buildWedgePath(WHEEL_CX, WHEEL_CY, GANDANTA_INNER, GANDANTA_OUTER, zone.startDeg, zone.endDeg)}
            fill="rgba(255,30,30,0.2)" filter="url(#gandantaGlow)"
          />
        );
      })}
    </g>
  );
};

// ── Moon Phase Icon ─────────────────────────────────────────────────
const MoonPhaseIcon: React.FC<{
  cx: number; cy: number; radius: number;
  illumination: number; isWaxing: boolean;
}> = ({ cx, cy, radius, illumination, isWaxing }) => {
  const r = radius;
  const innerR = r * Math.abs(2 * illumination - 1);
  const sweep = illumination > 0.5;
  const topY = cy - r;
  const botY = cy + r;

  if (illumination < 0.01) {
    return <circle cx={cx} cy={cy} r={r} fill="#222" stroke="#555" strokeWidth={1} />;
  }
  if (illumination > 0.99) {
    return <circle cx={cx} cy={cy} r={r} fill="#F5F5DC" stroke="#ddd" strokeWidth={0.5} filter="url(#planetGlow)" />;
  }

  // Outer semicircle: waxing lights right side (sweep=1), waning lights left (sweep=0)
  const outerFlag = isWaxing ? 1 : 0;
  // Inner terminator arc: same direction as outer for crescent, opposite for gibbous
  const innerFlag = sweep ? (isWaxing ? 1 : 0) : (isWaxing ? 0 : 1);
  const path = [
    `M ${cx} ${topY}`,
    `A ${r} ${r} 0 0 ${outerFlag} ${cx} ${botY}`,
    `A ${innerR} ${r} 0 0 ${innerFlag} ${cx} ${topY}`,
    'Z',
  ].join(' ');

  return (
    <g>
      <circle cx={cx} cy={cy} r={r} fill="#222" stroke="#555" strokeWidth={0.5} />
      <path d={path} fill="#F5F5DC" filter="url(#planetGlow)" />
    </g>
  );
};

// ── Planet Marker — Sanskrit abbreviation on circle ─────────────────
const PlanetMarker: React.FC<{
  planet: PlanetPosition;
  radialOffset: number;
  moonPhase?: { illumination: number; isWaxing: boolean } | null;
  isRetrograde?: boolean;
  inGandanta?: boolean;
}> = ({ planet, radialOffset, moonPhase, isRetrograde, inGandanta }) => {
  const angle = degToAngle(planet.longitude_degrees);
  const placementR = MID_R + radialOffset;
  const pos = polarToXY(WHEEL_CX, WHEEL_CY, placementR, angle);
  const color = PLANET_COLORS[planet.planet_english] || '#fff';
  const abbr = PLANET_SANSKRIT_ABBR[planet.planet_english] || planet.planet_english.slice(0, 2);
  const isMoon = planet.planet_english === 'Moon';
  const r = PLANET_CIRCLE_R;

  const fullName = PLANET_SANSKRIT_NAMES[planet.planet_english] || planet.planet_english;
  const ariaLabel = `${fullName} (${planet.planet_english}) at ${planet.longitude_degrees.toFixed(1)}° in ${planet.zodiac_sign}${isRetrograde ? ', retrograde' : ''}${inGandanta ? ', in gandanta zone' : ''}`;

  return (
    <g role="img" aria-label={ariaLabel}>
      <title>{ariaLabel}</title>
      {inGandanta && (
        <circle
          cx={pos.x} cy={pos.y} r={r + 5}
          fill="none" stroke="#ff3333" strokeWidth={1.5}
          strokeDasharray="3,3" opacity={0.7}
        />
      )}
      {isRetrograde && (
        <circle
          cx={pos.x} cy={pos.y} r={r + 2.5}
          fill="none" stroke="#ff4444" strokeWidth={2}
          opacity={0.85}
        />
      )}
      {isMoon && moonPhase ? (
        <MoonPhaseIcon
          cx={pos.x} cy={pos.y} radius={r}
          illumination={moonPhase.illumination} isWaxing={moonPhase.isWaxing}
        />
      ) : (
        <circle
          cx={pos.x} cy={pos.y} r={r}
          fill={color} filter="url(#planetGlow)" opacity={0.9}
        />
      )}
      {/* The Moon's disc is part cream, part dark: a cream halo keeps its
          label legible on both halves (was #888, unreadable on the lit side). */}
      <text
        x={pos.x} y={pos.y + 5}
        textAnchor="middle"
        fill="#000"
        stroke={isMoon ? 'rgba(245,245,220,0.95)' : undefined}
        strokeWidth={isMoon ? 3 : undefined}
        paintOrder={isMoon ? 'stroke' : undefined}
        fontSize={13} fontWeight={700}
        fontFamily={FONT}
      >
        {abbr}
      </text>
    </g>
  );
};

// ── Conjunction Label — Sanskrit abbreviations, stacked layout ──────
// The degree labels (0°, 30°, …) sit just inside the yuti label's radius, so a
// yuti near a sign boundary printed on top of one ("Ku – Gu120°"). Nudge the
// label along the ring until it is YUTI_LABEL_MIN_GAP° clear of the boundary.
const YUTI_LABEL_MIN_GAP = 7;
export const yutiLabelLongitude = (avgLongitude: number): number => {
  const lon = ((avgLongitude % 360) + 360) % 360;
  const nearest = Math.round(lon / 30) * 30;
  const off = lon - nearest;
  if (Math.abs(off) >= YUTI_LABEL_MIN_GAP) return lon;
  const nudged = nearest + (off < 0 ? -YUTI_LABEL_MIN_GAP : YUTI_LABEL_MIN_GAP);
  return ((nudged % 360) + 360) % 360;
};

const ConjunctionLabel: React.FC<{
  planets: PlanetPosition[];
  avgLongitude: number;
}> = ({ planets, avgLongitude }) => {
  const angle = degToAngle(yutiLabelLongitude(avgLongitude));
  const labelPos = polarToXY(WHEEL_CX, WHEEL_CY, CONJUNCTION_LABEL_R, angle);
  const names = planets.map((p) => PLANET_SANSKRIT_ABBR[p.planet_english] || p.planet_english.slice(0, 2));

  return (
    <g>
      <text
        x={labelPos.x} y={labelPos.y - 8}
        textAnchor="middle" dominantBaseline="central"
        fill="#FFD700" fontSize={13}
        fontFamily={FONT} fontWeight={600}
      >
        Yuti
      </text>
      <text
        x={labelPos.x} y={labelPos.y + 8}
        textAnchor="middle" dominantBaseline="central"
        fill="#FFD700" fontSize={12}
        fontFamily={FONT} fontStyle="italic" opacity={0.8}
      >
        {names.join(' – ')}
      </text>
    </g>
  );
};

// ── Elemental Panel (left side) ─────────────────────────────────────
// Legend style — small colored square swatch + label + planet list per
// element, matching the interactive Kāla Chakra left rail (LeftPanels HTML
// version) rather than the old full-width filled cards. Keep TATTVA_* layout
// constants in sync with computeElementalPanelHeight below.
const TATTVA_LABEL_H = 22;   // swatch + label row height
const TATTVA_LINE_H = 20;    // per planet-list line
const TATTVA_ROW_GAP = 10;   // gap between element rows
const TATTVA_MAX_PER_LINE = 4;

const ElementalPanel: React.FC<{ planets: PlanetPosition[]; topY?: number }> = ({ planets, topY }) => {
  const elements = classifyPlanetsByElement(planets);
  const panelX = 40;
  const panelY = topY ?? (TITLE_HEIGHT + 30);

  return (
    <g>
      <text
        x={panelX} y={panelY - 8}
        fill={SAFFRON} fontSize={18} fontWeight={700}
        fontFamily={FONT_FRAUNCES}
      >
        Tattva
      </text>
      {elements.map((el, i) => {
        const elColors = ELEMENT_COLORS[el.element];
        const sanskritName =
          el.element === 'Fire' ? 'Agni' :
          el.element === 'Earth' ? 'Pṛthvī' :
          el.element === 'Air' ? 'Vāyu' : 'Jala';

        const planetLines: string[][] = [];
        for (let j = 0; j < el.planets.length; j += TATTVA_MAX_PER_LINE) {
          planetLines.push(el.planets.slice(j, j + TATTVA_MAX_PER_LINE));
        }

        let yOffset = 8;
        for (let k = 0; k < i; k++) {
          const prevLines = Math.max(Math.ceil(elements[k].planets.length / TATTVA_MAX_PER_LINE), 1);
          yOffset += TATTVA_LABEL_H + prevLines * TATTVA_LINE_H + TATTVA_ROW_GAP;
        }
        const y = panelY + yOffset;

        return (
          <g key={el.element}>
            {/* Colored square swatch — the legend marker (fill = element hue) */}
            <rect
              x={panelX} y={y} width={16} height={16}
              rx={3} fill={elColors.text}
            />
            <text
              x={panelX + 26} y={y + 13}
              fill="rgba(255,255,255,0.92)" fontSize={16} fontWeight={600}
              fontFamily={FONT}
            >
              {sanskritName} ({el.element}) - {el.count}
            </text>
            {planetLines.map((line, li) => (
              <text
                key={`el-${el.element}-${li}`}
                x={panelX + 26} y={y + TATTVA_LABEL_H + 14 + li * TATTVA_LINE_H}
                fill="rgba(255,255,255,0.6)" fontSize={14}
                fontFamily={FONT}
              >
                {line.join(', ')}
              </text>
            ))}
            {el.planets.length === 0 && (
              <text
                x={panelX + 26} y={y + TATTVA_LABEL_H + 14}
                fill="rgba(255,255,255,0.35)" fontSize={14}
                fontFamily={FONT} fontStyle="italic"
              >
                -
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
};

// ── Compute position summary panel height ───────────────────────────
const computePositionSummaryHeight = (planetCount: number): number => {
  const lineH = 28;
  const legendH = 80;
  return 58 + planetCount * lineH + legendH + 8;
};

// ── Compute Tattva (elemental) panel height ─────────────────────────
// Mirrors ElementalPanel's internal cumulative layout so a following block
// (the static Pañcāṅga line on the capture) can be placed below it.
const computeElementalPanelHeight = (planets: PlanetPosition[]): number => {
  const elements = classifyPlanetsByElement(planets);
  let off = 8; // initial gap below the "Tattva" header (matches ElementalPanel)
  for (let i = 0; i < elements.length; i++) {
    const lines = Math.max(Math.ceil(elements[i].planets.length / TATTVA_MAX_PER_LINE), 1);
    off += TATTVA_LABEL_H + lines * TATTVA_LINE_H;
    if (i < elements.length - 1) off += TATTVA_ROW_GAP;
  }
  return off;
};

// ── Highlights: severity order + palette markers ────────────────────
// Good news first, then yutis, then cautions. Markers use the style guide's
// semantic colours (favourable green, avoid red; neutral for info) and differ
// in shape too, so colour is never the only cue. Replaces OS emoji (⚠️ ✅ ℹ️).
const SEVERITY_ORDER: Record<InsightSeverity, number> = { auspicious: 0, info: 1, caution: 2 };
export const sortBySeverity = <T extends { severity: InsightSeverity }>(items: T[]): T[] =>
  items.map((it, i) => ({ it, i }))
    .sort((a, b) => SEVERITY_ORDER[a.it.severity] - SEVERITY_ORDER[b.it.severity] || a.i - b.i)
    .map(({ it }) => it);

const SEVERITY_LABEL: Record<InsightSeverity, string> = { auspicious: 'Favourable', info: 'Note', caution: 'Caution' };
const SeverityMarker: React.FC<{ severity: InsightSeverity }> = ({ severity }) => (
  <svg width={10} height={10} viewBox="0 0 10 10" role="img" aria-label={SEVERITY_LABEL[severity]} style={{ flexShrink: 0, alignSelf: 'center' }}>
    {severity === 'auspicious' && <circle cx={5} cy={5} r={4.5} fill="#1e9e6a" />}
    {severity === 'caution' && <path d="M5 0.6 L9.6 9.2 H0.4 Z" fill="#c94f2d" />}
    {severity === 'info' && <rect x={1.5} y={1.5} width={7} height={7} transform="rotate(45 5 5)" fill="none" stroke="#a49dc4" strokeWidth={1.4} />}
  </svg>
);

// ── Compact Pañcāṅga one-liner: {Śu./Kṛ.}{Tithi} · {Nakṣatra} · {Vāra} ─
// Shared by the interactive left-rail summary and the static SVG capture block.
const buildPanchangaLine = (pd?: { paksha?: string; tithi?: string; nakshatra?: string; vaara?: string } | null): string => {
  if (!pd || !pd.tithi) return '';
  const lead = (s?: string) => (s || '').split(' till ')[0].split(',')[0].trim();
  const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
  const pakshaAbbr = isKrishnaPaksha(pd.paksha || '') ? 'Kṛ.' : 'Śu.';
  return `${pakshaAbbr}${cap(lead(pd.tithi))} · ${lead(pd.nakshatra)} · ${pd.vaara || ''}`.trim();
};

// ── Static Pañcāṅga block (SVG) — rides the Twitter capture ──────────
// Values only (tithi · nakṣatra · vāra + pakṣa), no hyperlink/interactivity.
// Ujjain-locked on the capture route (matches the narrative + Pillow image).
const PanchangaBlock: React.FC<{ line: string; topY: number }> = ({ line, topY }) => {
  const panelX = 40;
  const panelW = 460;
  return (
    <g>
      <text
        x={panelX} y={topY - 8}
        fill={SAFFRON} fontSize={18} fontWeight={700}
        fontFamily={FONT_FRAUNCES}
      >
        Pañcāṅga
      </text>
      <rect
        x={panelX} y={topY} width={panelW} height={44}
        rx={6} fill="rgba(10,10,30,0.65)" stroke="rgba(255,215,0,0.3)" strokeWidth={0.5}
      />
      <text
        x={panelX + 14} y={topY + 28}
        fill="rgba(255,255,255,0.9)" fontSize={18} fontWeight={500}
        fontFamily={FONT}
      >
        {line}
      </text>
    </g>
  );
};

// ── Left Panels (positions on top + elemental below) ────────────────
const LeftPanels: React.FC<{ planets: PlanetPosition[]; panchangaLine?: string }> = ({ planets, panchangaLine }) => {
  const posTopY = TITLE_HEIGHT + 30;
  const posH = computePositionSummaryHeight(planets.length);
  // Keep the whitespace above each section header consistent (~33px). The
  // Graha Sthāna panel bottom carries less internal padding than the tight
  // Tattva content bottom, so its offset is smaller than the Pañcāṅga one.
  const elemTopY = posTopY + posH + 35;
  const panchangaTopY = elemTopY + computeElementalPanelHeight(planets) + 46;
  return (
    <g>
      <PositionSummary planets={planets} topY={posTopY} />
      <ElementalPanel planets={planets} topY={elemTopY} />
      {panchangaLine && <PanchangaBlock line={panchangaLine} topY={panchangaTopY} />}
    </g>
  );
};

// ── Position Summary — Sanskrit primary, retrograde legend ──────────
const PLANET_ORDER: Record<string, number> = {
  Sun: 0, Moon: 1, Mars: 2, Mercury: 3, Jupiter: 4, Venus: 5, Saturn: 6, Rahu: 7, Ketu: 8,
};

const PositionSummary: React.FC<{ planets: PlanetPosition[]; topY: number }> = ({ planets, topY }) => {
  const sorted = [...planets].sort(
    (a, b) => (PLANET_ORDER[a.planet_english] ?? 99) - (PLANET_ORDER[b.planet_english] ?? 99),
  );
  const panelX = 40;
  const panelY = topY;
  const lineH = 28;
  const panelW = 460;
  const legendH = 80;
  const panelH = 58 + sorted.length * lineH + legendH + 8;

  return (
    <g>
      <rect
        x={panelX} y={panelY} width={panelW} height={panelH}
        rx={6} fill="rgba(10,10,30,0.65)" stroke="rgba(255,215,0,0.3)" strokeWidth={0.5}
      />
      <text
        x={panelX + 10} y={panelY + 24}
        fill={SAFFRON} fontSize={20} fontWeight={700}
        fontFamily={FONT_FRAUNCES}
      >
        Graha Sthāna
      </text>
      {/* Column headers */}
      <text x={panelX + 30} y={panelY + 46} fill={SAFFRON_MUTED} fontSize={14} fontWeight={600} fontFamily={FONT}>Graha</text>
      <text x={panelX + 140} y={panelY + 46} fill={SAFFRON_MUTED} fontSize={14} fontWeight={600} fontFamily={FONT}>Nakṣatra</text>
      <text x={panelX + 260} y={panelY + 46} fill={SAFFRON_MUTED} fontSize={14} fontWeight={600} fontFamily={FONT}>Rāśi</text>
      <text x={panelX + 352} y={panelY + 46} fill={SAFFRON_MUTED} fontSize={14} fontWeight={600} fontFamily={FONT_MONO}>Deg</text>
      <line
        x1={panelX + 10} y1={panelY + 51}
        x2={panelX + panelW - 10} y2={panelY + 51}
        stroke="rgba(255,215,0,0.2)" strokeWidth={0.5}
      />
      {sorted.map((p, i) => {
        const y = panelY + 66 + i * lineH;
        const color = PLANET_COLORS[p.planet_english] || '#fff';
        const isRetro = p.motion === 'RETROGRADE' || ALWAYS_RETROGRADE.has(p.planet_english);
        const dms = p.degrees_in_sign_dms;
        const nakIdx = Math.floor(p.longitude_degrees / 13.333) % 27;
        const nakName = NAKSHATRA_DATA[nakIdx]?.shortName || '';
        return (
          <g key={p.planet_english}>
            <circle cx={panelX + 16} cy={y + 6} r={6} fill={color} opacity={1} />
            <text
              x={panelX + 30} y={y + 10}
              fill="rgba(255,255,255,1)" fontSize={18}
              fontFamily={FONT} fontWeight={600}
            >
              {p.planet_sanskrit}
              {/* Vakri (retrograde) mark — small red R beside the graha name,
                  matching the interactive Kāla Chakra rail (was far-right col). */}
              {isRetro && (
                <tspan dx={4} fill="#ff4444" fontSize={12} fontWeight={700}>R</tspan>
              )}
            </text>
            <text
              x={panelX + 140} y={y + 10}
              fill="rgba(255,255,255,0.85)" fontSize={15}
              fontFamily={FONT}
            >
              {nakName}
            </text>
            <text
              x={panelX + 260} y={y + 10}
              fill="rgba(255,255,255,0.85)" fontSize={15}
              fontFamily={FONT}
            >
              {p.zodiac_sign_sanskrit}
            </text>
            <text
              x={panelX + 352} y={y + 10}
              fill="rgba(255,255,255,0.9)" fontSize={15}
              fontFamily={FONT_MONO}
            >
              {String(dms.degrees).padStart(2, ' ')}°{String(dms.minutes).padStart(2, '0')}&#39;{String(dms.seconds).padStart(2, '0')}&#34;
            </text>
          </g>
        );
      })}
      {/* Separator before legend */}
      <line
        x1={panelX + 10} y1={panelY + 66 + sorted.length * lineH + 4}
        x2={panelX + panelW - 10} y2={panelY + 66 + sorted.length * lineH + 4}
        stroke="rgba(255,255,255,0.25)" strokeWidth={0.5}
      />
      {/* Gandanta zone legend */}
      <g transform={`translate(${panelX + 10}, ${panelY + 66 + sorted.length * lineH + 18})`}>
        <rect x={0} y={-5} width={14} height={9} rx={2}
          fill="rgba(255,30,30,0.35)" stroke="#ff3333" strokeWidth={0.8} />
        <text x={20} y={4} fill="rgba(255,255,255,0.75)" fontSize={12} fontFamily={FONT}>
          Gandānta Zone
        </text>
      </g>
      {/* Planet in gandanta legend */}
      <g transform={`translate(${panelX + 10}, ${panelY + 66 + sorted.length * lineH + 40})`}>
        <circle cx={6} cy={0} r={7}
          fill="none" stroke="#ff3333" strokeWidth={1.5} strokeDasharray="3,3" />
        <text x={20} y={4} fill="rgba(255,255,255,0.75)" fontSize={12} fontFamily={FONT}>
          Planet in Gandānta
        </text>
      </g>
      {/* Retrograde legend */}
      <g transform={`translate(${panelX + 10}, ${panelY + 66 + sorted.length * lineH + 62})`}>
        <circle cx={6} cy={0} r={6} fill="#888" opacity={0.8} />
        <circle cx={6} cy={0} r={8} fill="none" stroke="#ff4444" strokeWidth={1.5} opacity={1} />
        <text x={20} y={4} fill="rgba(255,255,255,0.75)" fontSize={12} fontFamily={FONT}>
          Red ring = Vakri (Retrograde)
        </text>
      </g>
    </g>
  );
};

// ── Title Bar ───────────────────────────────────────────────────────

const TitleBar: React.FC<{ dateStr?: string; location?: string; capturedAt?: string }> = ({ dateStr, location, capturedAt }) => (
  <g>
    <rect x={0} y={0} width={WIDTH} height={TITLE_HEIGHT} fill="rgba(10,10,30,0.6)" />
    <line x1={0} y1={TITLE_HEIGHT} x2={WIDTH} y2={TITLE_HEIGHT} stroke="rgba(255,215,0,0.3)" strokeWidth={1} />
    <text
      x={WIDTH / 2} y={52} textAnchor="middle" dominantBaseline="central"
      fill={SAFFRON} fontSize={40} fontWeight={700}
      fontFamily={FONT_FRAUNCES}
    >
      Kāla Chakra
    </text>
    <text
      x={60} y={30} fill="rgba(255,255,255,0.95)" fontSize={16}
      fontFamily={FONT}
    >
      Graha Sthiti (Sidereal)
    </text>
    {capturedAt && (
      <text
        x={60} y={50} fill="rgba(255,255,255,0.85)" fontSize={14}
        fontFamily={FONT}
      >
        Captured at {capturedAt}
      </text>
    )}
    <text
      x={WIDTH - 60} y={35} textAnchor="end" fill="rgba(255,255,255,1)"
      fontSize={22} fontWeight={600} fontFamily={FONT}
    >
      {formatDate(dateStr)}
    </text>
    <text
      x={WIDTH - 60} y={58} textAnchor="end" fill="rgba(255,255,255,0.9)"
      fontSize={16} fontFamily={FONT}
    >
      {location || 'Ujjain, India'}
    </text>
    <text
      x={WIDTH - 60} y={78} textAnchor="end" fill="rgba(255,255,255,0.8)"
      fontSize={14} fontFamily={FONT}
    >
      Lahiri Ayanamsa
    </text>
  </g>
);

// ── Anti-clockwise direction indicator (inside inner ring) ──────────
const DirectionArrow: React.FC = () => {
  return (
    <text
      x={WHEEL_CX} y={WHEEL_CY}
      textAnchor="middle" dominantBaseline="central"
      fill="rgba(255,215,0,0.8)" fontSize={16}
      fontFamily={FONT}
    >
      ↺ Anti-clockwise
    </text>
  );
};

// ── Watermark ───────────────────────────────────────────────────────
const WATERMARK_STYLE = { fill: 'rgba(255,255,255,0.2)', fontSize: 14, fontFamily: FONT };
const Watermark: React.FC = () => (
  <g>
    {/* Top-left of wheel area */}
    <text
      x={500} y={TITLE_HEIGHT + 20}
      {...WATERMARK_STYLE}
    >
      kaalagyana.com
    </text>
    {/* Center of wheel */}
    <text
      x={WHEEL_CX} y={WHEEL_CY - 14} textAnchor="middle"
      {...WATERMARK_STYLE}
    >
      kaalagyana.com
    </text>
    {/* Bottom-right of wheel area */}
    <text
      x={WHEEL_CX + NAKSHATRA_OUTER_R + 20} y={HEIGHT - 15} textAnchor="end"
      {...WATERMARK_STYLE}
    >
      kaalagyana.com
    </text>
  </g>
);

// ── Compute radial stacking offsets (adaptive) ──────────────────────
// Only push planets radially when their arc distance is too small to fit
const computeRadialOffsets = (planets: PlanetPosition[]): Record<string, number> => {
  const offsets: Record<string, number> = {};
  const placed: { name: string; deg: number; radial: number }[] = [];
  const sorted = [...planets].sort((a, b) => a.longitude_degrees - b.longitude_degrees);

  // Arc distance in pixels between two longitudes at MID_R
  const arcDist = (deg1: number, deg2: number): number => {
    let diff = Math.abs(deg2 - deg1);
    if (diff > 180) diff = 360 - diff;
    return (diff / 360) * 2 * Math.PI * MID_R;
  };

  // Check if a candidate position collides with any already-placed planet
  const collides = (deg: number, radial: number): boolean =>
    placed.some((p) => {
      const ax = arcDist(deg, p.deg);
      const ry = Math.abs(radial - p.radial);
      return Math.sqrt(ax * ax + ry * ry) < MIN_PLANET_GAP;
    });

  for (const planet of sorted) {
    const deg = planet.longitude_degrees;
    // Try offset 0 first
    if (!collides(deg, 0)) {
      offsets[planet.planet_english] = 0;
      placed.push({ name: planet.planet_english, deg, radial: 0 });
    } else {
      // Try alternating outward/inward at increasing steps
      let found = false;
      for (let step = MIN_PLANET_GAP; step <= 60; step += 4) {
        if (!collides(deg, step)) {
          offsets[planet.planet_english] = step;
          placed.push({ name: planet.planet_english, deg, radial: step });
          found = true;
          break;
        }
        if (!collides(deg, -step)) {
          offsets[planet.planet_english] = -step;
          placed.push({ name: planet.planet_english, deg, radial: -step });
          found = true;
          break;
        }
      }
      if (!found) {
        offsets[planet.planet_english] = 0;
        placed.push({ name: planet.planet_english, deg, radial: 0 });
      }
    }
  }

  return offsets;
};

// ── Main Component ──────────────────────────────────────────────────
// Ujjain coordinates (traditional reference point for Indian astronomy)
const UJJAIN = { latitude: 23.1765, longitude: 75.7885, altitude: 494 };

// Convert local Date to formatted strings
const toLocalDateStr = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};
const toLocalTimeStr = (d: Date): string => {
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  const s = String(d.getSeconds()).padStart(2, '0');
  return `${h}:${m}:${s}`;
};
// Convert location-local date+time → UTC, using the displayed location's
// timezone offset (hours, e.g. 5.5 for IST). When the wheel is viewing a
// remote location, the time picker means "time at that location" — not in
// the user's browser timezone.
const locationToUTC = (dateStr: string, timeStr: string, tzOffsetHours: number): { utcDate: string; utcTime: string; utcFull: string } => {
  const utcMs = Date.parse(`${dateStr}T${timeStr}Z`) - tzOffsetHours * 3600 * 1000;
  const utc = new Date(utcMs);
  const utcDate = utc.toISOString().slice(0, 10);
  const utcTime = utc.toISOString().slice(11, 19);
  return { utcDate, utcTime, utcFull: `${utcDate} ${utcTime} UTC` };
};
// Caption under the title. A wheel locked to sunrise names only the event:
// the sunrise time changes every day and the date is already in the header
// (B-140). Otherwise "Bangalore, India sunrise · 06:39 IST" at sunrise, else
// the raw UTC sample.
export const captionFor = ({ atSunrise, lockedToSunrise, locationName, sunriseHHMM, tzLabel, utcFull }: {
  atSunrise: boolean; lockedToSunrise: boolean; locationName: string;
  sunriseHHMM?: string; tzLabel: string; utcFull: string;
}): string => {
  if (!atSunrise) return utcFull;
  if (lockedToSunrise) return `${locationName.split(',')[0]} sunrise`;
  return `${locationName} sunrise · ${sunriseHHMM} ${tzLabel}`;
};
// Convert UTC date+time to IST date string for Ujjain display
const utcToISTDate = (utcDate: string, utcTime: string): string => {
  const utc = new Date(`${utcDate}T${utcTime}Z`);
  const istMs = utc.getTime() + 5.5 * 60 * 60 * 1000;
  const ist = new Date(istMs);
  return ist.toISOString().slice(0, 10);
};

interface AlignmentLocation {
  latitude: number;
  longitude: number;
  altitude?: number;
  name?: string;
  country?: string;
}

const PlanetaryAlignmentCapture: React.FC<{
  fixedSize?: boolean;
  mini?: boolean;
  initialDate?: string;
  initialTime?: string;
  location?: AlignmentLocation | null;
  tzOffset?: number;             // Hours from UTC for the displayed location (e.g., 5.5 for IST)
  sunriseLocal?: string;         // Sunrise at displayed location in HH:MM local time (from panchanga API)
  lockToLocationSunrise?: boolean; // Full Chart mode: always sample at the displayed location's sunrise
  showPanels?: boolean;
  ujjainReading?: boolean;       // Merged page: show the Ujjain Jyotiṣa Dṛṣṭi narrative (controlled from the top control bar). Default on.
  onViewDailyPanchanga?: () => void;
  onViewPlanetaryPositions?: () => void;  // Merged page: link from Graha Sthāna rail to the detailed Planetary Positions page.
  onClick?: () => void;
}> = ({ fixedSize = false, mini = false, initialDate, initialTime, location, tzOffset, sunriseLocal, lockToLocationSunrise = false, showPanels = true, ujjainReading = true, onViewDailyPanchanga, onViewPlanetaryPositions, onClick }) => {
  const now = useMemo(() => new Date(), []);
  const [selectedDate, setSelectedDate] = useState(() => initialDate || toLocalDateStr(now));
  const [selectedTime, setSelectedTime] = useState(() => initialTime || toLocalTimeStr(now));
  // Ujjain Jyotiṣa Dṛṣṭi reading toggle (merged page) — controlled by the top control bar.
  const showUjjainReading = ujjainReading;

  // Sync with parent date/time when they change
  useEffect(() => {
    if (initialDate) setSelectedDate(initialDate);
  }, [initialDate]);

  useEffect(() => {
    if (initialTime) setSelectedTime(initialTime);
  }, [initialTime]);

  // Resolve location: use prop or fall back to Ujjain
  const activeLocation = useMemo(() => ({
    latitude: location?.latitude ?? UJJAIN.latitude,
    longitude: location?.longitude ?? UJJAIN.longitude,
    altitude: location?.altitude ?? UJJAIN.altitude,
    name: location?.name ? `${location.name}, ${location.country || ''}`.replace(/, $/, '') : 'Ujjain, India',
  }), [location]);

  // Panchanga for the selected date (always English for compact display).
  // Fetched first so its sunrise + timezone can drive the wheel's sample point.
  const { data: panchangaData } = usePanchanga({
    date: selectedDate,
    latitude: activeLocation.latitude,
    longitude: activeLocation.longitude,
    altitude: activeLocation.altitude,
    locale: 'en',
  });

  // Effective sunrise + tz: prefer externally supplied, else use this
  // component's own panchanga fetch (which is for activeLocation).
  const effectiveSunrise = sunriseLocal ?? panchangaData?.sunrise;
  const effectiveTzOffset = tzOffset ?? panchangaData?.location?.timezone ?? 5.5;

  // Full Chart mode: always sample at the displayed location's sunrise.
  // Overrides any inherited `initialTime` so wheel + insights agree.
  useEffect(() => {
    if (lockToLocationSunrise && effectiveSunrise) {
      const sr = effectiveSunrise.length === 5 ? `${effectiveSunrise}:00` : effectiveSunrise;
      setSelectedTime(sr);
    }
  }, [lockToLocationSunrise, effectiveSunrise]);

  // Convert selected location-local date/time → UTC for API
  const utc = useMemo(
    () => locationToUTC(selectedDate, selectedTime || '00:00:00', effectiveTzOffset),
    [selectedDate, selectedTime, effectiveTzOffset],
  );

  // IST date for Ujjain display on top right
  const istDate = useMemo(() => utcToISTDate(utc.utcDate, utc.utcTime), [utc]);

  const { data, loading, error } = usePlanetaryPositions({
    date: utc.utcDate,
    time: utc.utcTime,
    latitude: activeLocation.latitude,
    longitude: activeLocation.longitude,
    altitude: activeLocation.altitude,
  });
  // Caption: show "Ujjain sunrise · 05:40 IST" when the wheel is sampling at
  // sunrise (the canonical reference point), else the raw UTC capture string.
  const tzLabel = useMemo(() => {
    if (effectiveTzOffset === 5.5) return 'IST';
    const sign = effectiveTzOffset >= 0 ? '+' : '-';
    const abs = Math.abs(effectiveTzOffset);
    const h = Math.floor(abs);
    const m = Math.round((abs - h) * 60);
    return `UTC${sign}${h}${m ? `:${String(m).padStart(2, '0')}` : ''}`;
  }, [effectiveTzOffset]);
  const sunriseHHMM = effectiveSunrise?.slice(0, 5);
  const selectedHHMM = (selectedTime || '').slice(0, 5);
  const atSunrise = Boolean(sunriseHHMM && selectedHHMM === sunriseHHMM);
  const fetchedAt = captionFor({
    atSunrise, lockedToSunrise: lockToLocationSunrise, locationName: activeLocation.name,
    sunriseHHMM, tzLabel, utcFull: utc.utcFull,
  });
  // The tweeted Kāla Chakra must show the instant its narrative describes
  // (Ujjain sunrise, B-140); the screenshot Lambda waits for this flag so it
  // never shoots the first frame, which is sampled at the browser's clock, nor
  // the render where the time has moved but the old positions are still shown.
  const sampled = !loading && data?.date === utc.utcDate && data?.time === utc.utcTime;
  const captureReady = sampled && (!lockToLocationSunrise || atSunrise);

  const planets = useMemo(() => data?.planets || [], [data?.planets]);

  const sunPlanet = planets.find((p) => p.planet_english === 'Sun');
  const moonPlanet = planets.find((p) => p.planet_english === 'Moon');

  const moonPhase = useMemo(() => {
    if (!sunPlanet || !moonPlanet) return null;
    return computeMoonPhase(sunPlanet.longitude_degrees, moonPlanet.longitude_degrees);
  }, [sunPlanet, moonPlanet]);

  const conjunctions = useMemo(() => detectYutis(planets), [planets]);
  const radialOffsets = useMemo(() => computeRadialOffsets(planets), [planets]);
  const insights = useMemo(() => generateInsights(planets, panchangaData?.tithi), [planets, panchangaData?.tithi]);
  const sortedPlanets = useMemo(() =>
    [...planets].sort((a, b) => {
      const order: Record<string, number> = { Sun: 0, Moon: 1, Mars: 2, Mercury: 3, Jupiter: 4, Venus: 5, Saturn: 6, Rahu: 7, Ketu: 8 };
      return (order[a.planet_english] ?? 99) - (order[b.planet_english] ?? 99);
    }),
  [planets]);
  const elements = useMemo(() => classifyPlanetsByElement(planets), [planets]);

  const homeViewBox = { x: 470, y: TITLE_HEIGHT, w: 980, h: HEIGHT - TITLE_HEIGHT };
  const containerStyle: React.CSSProperties = fixedSize
    ? { width: WIDTH, height: HEIGHT, position: 'relative', overflow: 'hidden', background: '#0a0a1e' }
    : showPanels
      ? { maxWidth: '100%', height: 'calc(100vh - 120px)', aspectRatio: `${WIDTH} / ${HEIGHT}`, position: 'relative', overflow: 'hidden', background: '#0a0a1e', margin: '0 auto' }
      : { width: '100%', maxHeight: 'calc(100vh - 120px)', aspectRatio: `${homeViewBox.w} / ${homeViewBox.h}`, position: 'relative', overflow: 'hidden', background: '#0a0a1e' };

  if (loading) {
    if (mini) {
      return (
        <div style={{
          width: '100%', aspectRatio: '1 / 1', borderRadius: 16,
          background: 'radial-gradient(ellipse at center, #0c0c2a 0%, #060618 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#FFD700', fontSize: 14, fontFamily: FONT_SERIF,
        }}>
          Loading...
        </div>
      );
    }
    return (
      <div style={{
        ...containerStyle,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#FFD700', fontSize: 28, fontFamily: FONT_SERIF,
      }}>
        Loading Planetary Positions...
      </div>
    );
  }

  if (error || !data) {
    if (mini) return null;
    return (
      <div style={{
        ...containerStyle,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#ff4444', fontSize: 22, fontFamily: FONT,
      }}>
        {error || 'No data available'}
      </div>
    );
  }

  // ── Shared SVG wheel ─────────────────────────────────────────────
  const svgViewBox = showPanels
    ? `0 0 ${WIDTH} ${HEIGHT}`
    : `${homeViewBox.x} ${homeViewBox.y} ${homeViewBox.w} ${homeViewBox.h}`;

  const planetSummary = planets.map(p => `${p.planet_english} in ${p.zodiac_sign}`).join(', ');

  const wheelSvg = (
    <svg
      viewBox={svgViewBox}
      preserveAspectRatio={showPanels ? 'xMidYMid meet' : 'xMidYMin meet'}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
      role="img"
      aria-label={`Kala Chakra sidereal planetary wheel showing ${planets.length} grahas: ${planetSummary}`}
    >
      <title>Kala Chakra - Sidereal Planetary Positions</title>
      <desc>Interactive wheel visualization showing the current positions of {planets.length} Vedic grahas in the sidereal zodiac with nakshatra divisions.</desc>
      <SvgFilters />
      <BackgroundStars />
      {showPanels && <TitleBar dateStr={istDate} capturedAt={fetchedAt} location={activeLocation.name} />}

      {/* Wheel rings */}
      <circle cx={WHEEL_CX} cy={WHEEL_CY} r={OUTER_R}
        fill="rgba(10,10,30,0.3)" stroke="rgba(255,215,0,0.12)" strokeWidth={1} />
      <circle cx={WHEEL_CX} cy={WHEEL_CY} r={INNER_R}
        fill="rgba(5,5,20,0.5)" stroke="rgba(255,215,0,0.1)" strokeWidth={0.5} />

      {/* Zodiac segments */}
      {Array.from({ length: 12 }, (_, i) => i + 1).map((s) => (
        <ZodiacSegment key={`seg-${s}`} signNumber={s} />
      ))}
      <SignBoundaryLines />
      <NakshatraRing />
      {Array.from({ length: 12 }, (_, i) => i + 1).map((s) => (
        <ZodiacFigure key={`fig-${s}`} signNumber={s} />
      ))}
      {Array.from({ length: 12 }, (_, i) => i + 1).map((s) => (
        <ConstellationStars key={`const-${s}`} signNumber={s} />
      ))}
      {Array.from({ length: 12 }, (_, i) => i + 1).map((s) => (
        <ZodiacLabel key={`lbl-${s}`} signNumber={s} />
      ))}
      <GandantaOverlay />
      {planets.map((planet) => (
        <PlanetMarker
          key={planet.planet_english}
          planet={planet}
          radialOffset={radialOffsets[planet.planet_english] || 0}
          moonPhase={
            planet.planet_english === 'Moon' && moonPhase
              ? { illumination: moonPhase.illumination, isWaxing: moonPhase.isWaxing }
              : null
          }
          isRetrograde={planet.motion === 'RETROGRADE' || ALWAYS_RETROGRADE.has(planet.planet_english)}
          inGandanta={isInGandanta(planet.longitude_degrees)}
        />
      ))}
      {conjunctions.map((conj, i) => (
        <ConjunctionLabel key={`conj-${i}`} planets={conj.planets} avgLongitude={conj.avgLongitude} />
      ))}
      {showPanels && <LeftPanels planets={planets} panchangaLine={buildPanchangaLine(panchangaData)} />}
      <DirectionArrow />
      <Watermark />
    </svg>
  );

  // ── MINI MODE: compact wheel for embedding (e.g., landing page) ──
  if (mini) {
    const miniViewBox = `${homeViewBox.x} ${homeViewBox.y} ${homeViewBox.w} ${homeViewBox.h}`;
    return (
      <div
        onClick={onClick}
        style={{
          width: '100%',
          aspectRatio: '1 / 1',
          position: 'relative',
          overflow: 'hidden',
          background: 'radial-gradient(ellipse at center, #0c0c2a 0%, #060618 100%)',
          borderRadius: 16,
          cursor: onClick ? 'pointer' : undefined,
        }}
      >
        <svg
          viewBox={miniViewBox}
          preserveAspectRatio="xMidYMid meet"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
          role="img"
          aria-label={`Kala Chakra planetary wheel - ${planets.length} grahas in sidereal zodiac`}
        >
          <title>Kala Chakra - Live Planetary Positions</title>
          <SvgFilters />
          <BackgroundStars />
          <circle cx={WHEEL_CX} cy={WHEEL_CY} r={OUTER_R}
            fill="rgba(10,10,30,0.3)" stroke="rgba(255,215,0,0.12)" strokeWidth={1} />
          <circle cx={WHEEL_CX} cy={WHEEL_CY} r={INNER_R}
            fill="rgba(5,5,20,0.5)" stroke="rgba(255,215,0,0.1)" strokeWidth={0.5} />
          {Array.from({ length: 12 }, (_, i) => i + 1).map((s) => (
            <ZodiacSegment key={`seg-${s}`} signNumber={s} />
          ))}
          <SignBoundaryLines />
          <NakshatraRing />
          {Array.from({ length: 12 }, (_, i) => i + 1).map((s) => (
            <ZodiacFigure key={`fig-${s}`} signNumber={s} />
          ))}
          {Array.from({ length: 12 }, (_, i) => i + 1).map((s) => (
            <ConstellationStars key={`const-${s}`} signNumber={s} />
          ))}
          {Array.from({ length: 12 }, (_, i) => i + 1).map((s) => (
            <ZodiacLabel key={`lbl-${s}`} signNumber={s} />
          ))}
          <GandantaOverlay />
          {planets.map((planet) => (
            <PlanetMarker
              key={planet.planet_english}
              planet={planet}
              radialOffset={radialOffsets[planet.planet_english] || 0}
              moonPhase={
                planet.planet_english === 'Moon' && moonPhase
                  ? { illumination: moonPhase.illumination, isWaxing: moonPhase.isWaxing }
                  : null
              }
              isRetrograde={planet.motion === 'RETROGRADE' || ALWAYS_RETROGRADE.has(planet.planet_english)}
              inGandanta={isInGandanta(planet.longitude_degrees)}
            />
          ))}
          <DirectionArrow />
        </svg>
      </div>
    );
  }

  // ── MERGED VIEW: 3 top-aligned columns ───────────────────────────
  //   Left  = your-location data rail (Graha Sthāna, Tattva, Pañcāṅga,
  //           Highlights) — all follow the selected location. Gandānta is
  //           shown on the wheel (zones + dashed ring) and in Highlights.
  //   Center= the wheel (geometry is location-agnostic).
  //   Right = Ujjain Jyotiṣa Dṛṣṭi narrative, opt-in via the checkbox.
  if (!showPanels && !fixedSize) {
    const panchangaLine = buildPanchangaLine(panchangaData);
    return (
      <div style={{ display: 'flex', gap: 0, alignItems: 'stretch', height: `calc(100vh - ${MERGED_CHROME_H}px)` }}>
        {/* ── Dark instrument: control strip + left rail + wheel ── */}
        <div style={{
          flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
          background: 'linear-gradient(160deg, #080820 0%, #0c0c2a 30%, #0a0a22 60%, #060618 100%)',
        }}>
          {/* Title strip (mirrors the capture layout): sidereal caption left,
              centered title, date + location + ayanamsa right */}
          {/* Below ~600 px of column the title takes its own row (kc-title-strip in index.css,
              B-153-KCTITLE): pinned left/right, the caption ran under the title at ~700 px. */}
          <div className="kc-title-strip" style={{ position: 'relative', padding: '12px 16px 10px', textAlign: 'center', minHeight: 58 }}>
            <div className="kc-title-wrap">
              <div className="kc-title-side kc-title-left" style={{ fontFamily: FONT, lineHeight: 1.4 }}>
                <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13 }}>Graha Sthiti (Sidereal)</div>
                <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11 }}>Captured at {fetchedAt}</div>
              </div>
              <h2 className="kc-title-main" style={{ margin: 0, fontFamily: FONT_FRAUNCES, fontSize: 30, fontWeight: 500, color: SAFFRON, letterSpacing: 0.2 }}>
                Kāla Chakra
              </h2>
              <div className="kc-title-side kc-title-right" style={{ fontFamily: FONT, lineHeight: 1.4 }}>
                <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: 15, fontWeight: 600 }}>{formatDate(istDate)}</div>
                <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>{activeLocation.name}</div>
                <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11 }}>Lahiri Ayanamsa</div>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', flex: 1, minHeight: 0, alignItems: 'stretch' }}>
          {/* Left rail — location-following data. Line heights are whole pixels,
              and even wherever a shape is centred in the line: fractional ones
              (1.35 × 11px = 14.85px) and odd ones (8px dot in a 17px line) put
              dots, swatches and markers on half-pixel positions, whose anti-aliased edges
              varied between screenshot runs inside this scrolling layer (B-145). */}
          <div style={{
            width: 295, flexShrink: 0, padding: '16px 12px', overflowY: 'auto',
            color: 'rgba(255,255,255,0.9)', fontFamily: FONT, fontSize: 13,
          }}>
            <div style={{ fontSize: 18, lineHeight: '24px', color: SAFFRON, fontWeight: 500, marginBottom: 8, fontFamily: FONT_FRAUNCES }}>
              Graha Sthāna
            </div>
            {/* Column headers */}
            <div style={{ display: 'flex', alignItems: 'baseline', fontSize: 11, lineHeight: '17px', fontWeight: 600, color: SAFFRON_MUTED, marginBottom: 5, paddingBottom: 4, borderBottom: '1px solid rgba(232,149,31,0.25)', letterSpacing: 0.3 }}>
              <span style={{ width: 14, flexShrink: 0 }} />
              <span style={{ width: 66, flexShrink: 0 }}>Graha</span>
              <span style={{ width: 66, flexShrink: 0 }}>Nakṣatra</span>
              <span style={{ width: 52, flexShrink: 0 }}>Rāśi</span>
              <span style={{ marginLeft: 'auto' }}>Deg</span>
            </div>
            {sortedPlanets.map((p) => {
              const color = PLANET_COLORS[p.planet_english] || '#fff';
              const isRetro = p.motion === 'RETROGRADE' || ALWAYS_RETROGRADE.has(p.planet_english);
              const dms = p.degrees_in_sign_dms;
              const nakIdx = Math.floor(p.longitude_degrees / 13.333) % 27;
              const nakName = NAKSHATRA_DATA[nakIdx]?.shortName || '';
              return (
                <div key={p.planet_english} style={{ display: 'flex', alignItems: 'baseline', gap: 0, marginBottom: 4, lineHeight: '16px', height: 18, fontSize: 11 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0, alignSelf: 'center', marginRight: 6 }} />
                  <span style={{ fontWeight: 600, fontSize: 12, width: 66, flexShrink: 0 }}>
                    {p.planet_sanskrit}
                    {isRetro && <span style={{ color: '#ff4444', fontWeight: 700, marginLeft: 3, fontSize: 9 }}>R</span>}
                  </span>
                  <span style={{ color: 'rgba(255,255,255,0.7)', width: 66, flexShrink: 0 }}>{nakName}</span>
                  <span style={{ color: 'rgba(255,255,255,0.7)', width: 52, flexShrink: 0 }}>{p.zodiac_sign_sanskrit}</span>
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontFamily: FONT_MONO, marginLeft: 'auto', whiteSpace: 'nowrap', minWidth: 72, textAlign: 'right' }}>
                    {String(dms.degrees).padStart(2, '\u00A0')}°{String(dms.minutes).padStart(2, '0')}&#39;{String(dms.seconds).padStart(2, '0')}&quot;
                  </span>
                </div>
              );
            })}
            {/* Legend — Gandānta zone, planet in gandānta, retrograde */}
            <div style={{ marginTop: 8, fontSize: 10, lineHeight: '14px', color: 'rgba(255,255,255,0.7)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{ width: 12, height: 10, borderRadius: 2, background: 'rgba(255,30,30,0.35)', border: '1px solid #ff3333', display: 'inline-block', flexShrink: 0 }} />
                Gandānta Zone
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{ width: 12, height: 12, borderRadius: '50%', border: '1.5px dashed #ff3333', display: 'inline-block', flexShrink: 0 }} />
                Planet in Gandānta
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ position: 'relative', width: 12, height: 12, flexShrink: 0, display: 'inline-block' }}>
                  <span style={{ position: 'absolute', top: 2, left: 2, right: 2, bottom: 2, borderRadius: '50%', background: '#888' }} />
                  <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '1.5px solid #ff4444' }} />
                </span>
                Red ring = Vakri (Retrograde)
              </div>
            </div>
            {/* Link to the detailed Planetary Positions page — matches the Pañcāṅga CTA */}
            {onViewPlanetaryPositions && (
              <button
                onClick={onViewPlanetaryPositions}
                style={{ display: 'block', marginTop: 10, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: FONT, fontSize: 12, lineHeight: '16px', fontWeight: 600, color: SAFFRON, textAlign: 'left' }}
              >
                Detailed positions →
              </button>
            )}
            <div style={{ fontSize: 18, lineHeight: '24px', color: SAFFRON, fontWeight: 500, marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(232,149,31,0.18)', marginBottom: 10, fontFamily: FONT_FRAUNCES }}>
              Tattva
            </div>
            {elements.map((el) => {
              const elColors = ELEMENT_COLORS[el.element];
              const sanskritName = el.element === 'Fire' ? 'Agni' : el.element === 'Earth' ? 'Pṛthvī' : el.element === 'Air' ? 'Vāyu' : 'Jala';
              return (
                <div key={el.element} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                  <span style={{ width: 14, height: 14, borderRadius: 3, background: elColors.text, flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <div style={{ fontSize: 12.5, lineHeight: '16px', fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>{sanskritName} ({el.element}) - {el.count}</div>
                    <div style={{ fontSize: 10.5, lineHeight: '14px', color: 'rgba(255,255,255,0.6)' }}>
                      {el.planets.length > 0 ? el.planets.join(', ') : '-'}
                    </div>
                  </div>
                </div>
              );
            })}
            {/* Pañcāṅga summary — Śu./Kṛ.Tithi · Nakṣatra · Vāra; full detail behind CTA */}
            {panchangaLine && (
              <div style={{ marginTop: 20, borderTop: '1px solid rgba(232,149,31,0.18)', paddingTop: 14 }}>
                <div style={{ fontSize: 18, lineHeight: '24px', color: SAFFRON, fontWeight: 500, marginBottom: 8, fontFamily: FONT_FRAUNCES }}>
                  Pañcāṅga
                </div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.8)', fontFamily: FONT, lineHeight: '18px', marginBottom: onViewDailyPanchanga ? 10 : 0 }}>
                  {panchangaLine}
                </div>
                {onViewDailyPanchanga && (
                  <button
                    onClick={onViewDailyPanchanga}
                    style={{ display: 'block', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: FONT, fontSize: 12, lineHeight: '16px', fontWeight: 600, color: SAFFRON, textAlign: 'left' }}
                  >
                    Full Pañcāṅga for today →
                  </button>
                )}
              </div>
            )}
            {/* Highlights — local, always-on insight index (follows location) */}
            {insights.length > 0 && (
              <div style={{ marginTop: 20, borderTop: '1px solid rgba(232,149,31,0.18)', paddingTop: 14 }}>
                <div style={{ fontSize: 18, lineHeight: '24px', color: SAFFRON, fontWeight: 500, marginBottom: 8, fontFamily: FONT_FRAUNCES }}>
                  Highlights
                </div>
                {sortBySeverity(insights).map((insight, i) => (
                  <div key={i} style={{ fontSize: 12, marginBottom: 5, lineHeight: '16px', display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <SeverityMarker severity={insight.severity} />
                    <span>
                      <span style={{ fontWeight: 500 }}>{insight.title}</span>
                      {insight.category === 'chandrashtama' && (
                        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)' }}> - {insight.description.split(' — ')[1]}</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Center — wheel SVG */}
          <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
            {wheelSvg}
          </div>
          </div>
        </div>

        {/* ── Right rail — Ujjain narrative (opt-in), on app background ── */}
        {showUjjainReading && (
          <div style={{ width: 360, flexShrink: 0, padding: '20px 18px', overflowY: 'auto', maxHeight: `calc(100vh - ${MERGED_CHROME_H}px)` }}>
            <div style={{ marginBottom: 10 }}>
              <span className="inline-block rounded-full bg-muted text-muted-foreground" style={{ padding: '2px 10px', fontSize: 11, fontWeight: 600, letterSpacing: 0.3 }}>
                Ujjain · daily
              </span>
              <div className="text-muted-foreground" style={{ marginTop: 4, fontSize: 11, fontStyle: 'italic' }}>
                Cast for Ujjain at sunrise (early morning)
              </div>
            </div>
            <InsightsNarrative insights={insights} date={istDate} festivals={panchangaData?.festivals ?? undefined} />
          </div>
        )}
      </div>
    );
  }

  // ── SCREENSHOT MODE ───────────────────────────────────────────────
  if (fixedSize) {
    return (
      <div style={containerStyle} data-capture-ready={captureReady ? 'true' : 'false'}>
        {wheelSvg}
      </div>
    );
  }

  // ── FALLBACK (showPanels, non-capture) — wheel only ───────────────
  // The old "Full Chart" (Ujjain Jyotiṣa Dṛṣṭi) view has been folded into
  // the merged branch A above. Any remaining showPanels render (not fixedSize)
  // just shows the wheel; the narrative now lives in the merged left/right rails.
  return (
    <div style={containerStyle}>
      {wheelSvg}
    </div>
  );
};

export default PlanetaryAlignmentCapture;
